import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ActivarCuenta from './pages/ActivarCuenta';
import SolicitarRecuperacion from './pages/SolicitarRecuperacion';
import RestablecerContrasena from './pages/RestablecerContrasena';
import Superadmin from './pages/Superadmin';
import PaginaError from './components/PaginaError';
import ErrorBoundary from './components/ErrorBoundary';
import { GradienteMarca } from './components/icons';

// Solo en la compilación de DESARROLLO (REACT_APP_RUTAS_DE_PRUEBA=1; la de producción no la define y esta ruta no existe):
// /_prueba/500 se rompe a propósito para poder ver la pantalla 500 de ErrorBoundary.
function ExplotarAdrede() {
  throw new Error('Prueba de la pantalla 500 (solo desarrollo)');
}
const RUTAS_DE_PRUEBA = process.env.REACT_APP_RUTAS_DE_PRUEBA === '1';

// Rol guardado en la sesión; null si no hay o si el dato está dañado (nunca rompe la pantalla).
function leerRolGuardado() {
  try {
    const usuarioGuardado = sessionStorage.getItem('usuario');
    return usuarioGuardado ? JSON.parse(usuarioGuardado)?.rol || null : null;
  } catch (e) {
    return null;
  }
}

// rolesPermitidos es opcional: si no se pasa, alcanza con estar logueado.
// Si se pasa, además valida el rol guardado en sessionStorage y, si el usuario
// está logueado pero su rol no está permitido para esa ruta, muestra la pantalla
// 403 "Sin acceso" (antes lo mandaba a /dashboard sin explicar nada). Si el rol
// ni siquiera es uno que pueda usar la app (admin / operador), "Volver al inicio"
// cierra su sesión y lo lleva al login, para no dar vueltas en círculo.
//
// Token de sesión en sessionStorage (no localStorage) a propósito: se
// borra solo al cerrar la pestaña/ventana — ver utils/configurarAxios.js
// para el resto del criterio de sesión de municipio.
function RutaProtegida({ children, rolesPermitidos }) {
  const token = sessionStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;

  if (rolesPermitidos) {
    const rol = leerRolGuardado();
    if (!rolesPermitidos.includes(rol)) {
      return <PaginaError tipo={403} cerrarSesionAlVolver={!['admin', 'operador'].includes(rol)} />;
    }
  }

  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      {/* Degradé de marca global — se monta una sola vez acá y todos los
          íconos "bare" lo referencian con stroke="url(#degradeMarca)".
          Ver components/icons/index.jsx. */}
      <GradienteMarca />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/activar-cuenta/:token" element={<ActivarCuenta />} />
        <Route path="/recuperar-contraseña" element={<SolicitarRecuperacion />} />
        <Route path="/restablecer-contrasena/:token" element={<RestablecerContrasena />} />
        <Route path="/superadmin" element={<Superadmin />} />
        {/* A estas dos pantallas redirige el servidor cuando alguien ABRE en el navegador la dirección de un archivo subido
            (/api/uploads/...): sin sesión válida → 401 "Archivo protegido"; con sesión pero sin ese archivo → 404. Públicas. */}
        <Route path="/acceso-restringido" element={<PaginaError tipo={401} />} />
        <Route path="/no-encontrado" element={<PaginaError tipo={404} />} />
        <Route
          path="/dashboard"
          element={
            <RutaProtegida rolesPermitidos={['admin', 'operador']}>
              <Dashboard />
            </RutaProtegida>
          }
        />
        <Route path="/" element={<Navigate to="/login" replace />} />
        {RUTAS_DE_PRUEBA && <Route path="/_prueba/500" element={<ExplotarAdrede />} />}
        {/* Cualquier otra dirección, con o sin sesión: 404 "Página no encontrada" (antes mandaba al login sin explicar) */}
        <Route path="*" element={<PaginaError tipo={404} />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
